import React, { createContext, useContext, useReducer, useEffect } from 'react';
import authService from '../services/auth.service';

// Initial state
const initialState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  tenant: null
};

// Action types
const ActionType = {
  INITIALIZE: 'INITIALIZE',
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  UPDATE_USER: 'UPDATE_USER'
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case ActionType.INITIALIZE:
      return {
        ...state,
        isAuthenticated: action.payload.isAuthenticated,
        isLoading: false,
        user: action.payload.user,
        tenant: action.payload.tenant
      };
    case ActionType.LOGIN:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        tenant: action.payload.tenant
      };
    case ActionType.LOGOUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        tenant: null
      };
    case ActionType.UPDATE_USER:
      return {
        ...state,
        user: action.payload.user
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state
  useEffect(() => {
    const initialize = () => {
      try {
        const user = authService.getCurrentUser();
        const tenant = authService.getCurrentTenant();

        if (user) {
          dispatch({
            type: ActionType.INITIALIZE,
            payload: { isAuthenticated: true, user, tenant }
          });
        } else {
          authService.logout();
          dispatch({
            type: ActionType.INITIALIZE,
            payload: { isAuthenticated: false, user: null, tenant: null }
          });
        }
      } catch (error) {
        authService.logout();
        dispatch({
          type: ActionType.INITIALIZE,
          payload: { isAuthenticated: false, user: null, tenant: null }
        });
      }
    };

    initialize();
  }, []);

  // Login function
  const login = async (credentials) => {
    try {
      const response = await authService.login(credentials);
      
      if (response.success && response.data.user) {
        const { user } = response.data;
        const tenant = user.tenantId ? { id: user.tenantId, name: user.tenantName } : null;
        
        // Store in localStorage
        localStorage.setItem('user', JSON.stringify(user));
        if (tenant) {
          localStorage.setItem('tenant', JSON.stringify(tenant));
        }
        
        dispatch({
          type: ActionType.LOGIN,
          payload: { user, tenant }
        });
        
        return { success: true };
      } else {
        return { 
          success: false, 
          message: response.message || 'Login failed' 
        };
      }
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || error.message || 'Login failed' 
      };
    }
  };

  // Logout function - async so we await cookie deletion on backend
  const logout = async () => {
    try {
      await authService.logout();
      dispatch({ type: ActionType.LOGOUT });
    } catch (error) {
      console.error('Logout error:', error);
      dispatch({ type: ActionType.LOGOUT });
    }
  };

  // Update user profile in context
  const updateUserProfile = (updatedUser) => {
    const updatedUserData = { ...state.user, ...updatedUser };
    localStorage.setItem('user', JSON.stringify(updatedUserData));
    dispatch({
      type: ActionType.UPDATE_USER,
      payload: { user: updatedUserData }
    });
  };

  const value = {
    ...state,
    login,
    logout,
    updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;